import { Appointment } from "../models/admissions.model";
import type {
  EngageActivity,
  EngageReferral,
} from "../services/engage-activity.service";

function num(v: unknown): number {
  if (v == null || v === "") {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/**
 * Worklist Engage icon badge: prefer summed per-channel fields when API sends them;
 * otherwise fall back to `notification_count` (pending Engage notifications).
 */
export function worksheetRowEngageIconCountFromReferral(
  ref: EngageReferral | null | undefined
): number {
  if (!ref) {
    return 0;
  }
  const o = ref as Record<string, unknown>;
  const missed =
    num(o["missed_calls_count"]) +
    num(o["missed_call_count"]) +
    num(o["missedCount"]);
  const vm = num(o["voicemail_count"]) + num(o["voicemailCount"]);
  const msg =
    num(o["sms_count"]) +
    num(o["smsCount"]) +
    num(o["unread_message_count"]) +
    num(o["unread_messages"]) +
    num(o["message_count"]) +
    num(o["messages_count"]);
  const newRefs = num(o["new_referral_count"]) + num(o["newReferralsCount"]);
  const sum = missed + vm + msg + newRefs;
  if (sum > 0) {
    return sum;
  }
  return num(ref.notification_count);
}

/** Patient profile icon badge: pending reminder notifications (`reminder_notification_count`). */
export function worksheetRowReminderIconCountFromReferral(
  ref: EngageReferral | null | undefined
): number {
  if (!ref) {
    return 0;
  }
  const o = ref as Record<string, unknown>;
  const notif = num(o["reminder_notification_count"]);
  if (notif > 0) {
    return notif;
  }
  return num(ref.reminder_count);
}

/** SMS / unread message bucket for timeline `filter=unread_message`. */
export function worksheetRowUnreadMessageCountFromReferral(
  ref: EngageReferral | null | undefined
): number {
  if (!ref) {
    return 0;
  }
  const o = ref as Record<string, unknown>;
  return (
    num(o["sms_count"]) +
    num(o["smsCount"]) +
    num(o["unread_message_count"]) +
    num(o["unread_messages"]) +
    num(o["message_count"]) +
    num(o["messages_count"])
  );
}

/** Missed / voicemail / form-filled notification bucket for timeline `filter=notifications`. */
export function worksheetRowNotificationsChannelCountFromReferral(
  ref: EngageReferral | null | undefined
): number {
  if (!ref) {
    return 0;
  }
  const o = ref as Record<string, unknown>;
  return (
    num(o["missed_calls_count"]) +
    num(o["missed_call_count"]) +
    num(o["missedCount"]) +
    num(o["voicemail_count"]) +
    num(o["voicemailCount"]) +
    num(o["form_filled_count"]) +
    num(o["formFilledCount"]) +
    num(o["form_filled"])
  );
}

/**
 * When list-clinic returns an open/incomplete checklist count, surface it for the encounter icon badge.
 */
export function extractWorksheetChecklistOpenCountFromEncounter(
  encounter: Appointment
): number | undefined {
  const o = encounter as unknown as Record<string, unknown>;
  const keys = [
    "checklistOpenCount",
    "openChecklistCount",
    "incompleteChecklistCount",
    "open_checklist_items",
    "checklistIncompleteCount",
  ] as const;
  for (const k of keys) {
    if (o[k] != null && o[k] !== "") {
      return num(o[k]);
    }
  }
  return undefined;
}

export function formatWorksheetActionBadgeCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }
  if (value > 99) {
    return "99+";
  }
  return String(value);
}

function decCount(v: number | undefined | null): number {
  const n = Math.max(0, Math.floor(Number(v ?? 0)));
  return Math.max(0, n - 1);
}

/** Activity types with Engage timeline “Dismiss” that reduce the worklist Engage icon badge. */
export function isEngageDismissBadgeActivityType(activityType?: string | null): boolean {
  const t = String(activityType ?? "").trim().toLowerCase();
  return (
    t === "form_filled" ||
    t === "inbound" ||
    t === "outbound" ||
    t === "voicemail" ||
    t === "missed" ||
    t === "missed_call"
  );
}

/** Types counted in {@link worksheetRowNotificationsChannelCountFromReferral}. */
function isEngageNotificationsChannelDismissType(activityType?: string | null): boolean {
  const t = String(activityType ?? "").trim().toLowerCase();
  return (
    t === "form_filled" ||
    t === "voicemail" ||
    t === "missed" ||
    t === "missed_call" ||
    t === "inbound" ||
    t === "outbound"
  );
}

/** Engage activity row is SMS with a pending notification (`notification === 1`). */
export function isEngageSmsActivityWithNotification(
  activity: EngageActivity | Record<string, unknown> | null | undefined
): boolean {
  if (!activity || typeof activity !== "object") {
    return false;
  }
  const o = activity as Record<string, unknown>;
  const type = String(o["activityType"] ?? o["activity_type"] ?? o["type"] ?? "")
    .trim()
    .toLowerCase();
  if (type !== "sms") {
    return false;
  }
  const n = o["notification"];
  if (n === 1 || n === true) {
    return true;
  }
  return String(n ?? "").trim() === "1";
}

/** Count `activityType` / `type` = `sms` and `notification` = 1 from activity API response. */
export function countEngageSmsActivitiesWithNotification(
  activities: readonly (EngageActivity | Record<string, unknown>)[] | null | undefined
): number {
  if (!activities?.length) {
    return 0;
  }
  let count = 0;
  for (const row of activities) {
    if (isEngageSmsActivityWithNotification(row)) {
      count += 1;
    }
  }
  return count;
}

/**
 * After opening Activity + mark-active: subtract SMS pending count from total Engage badge.
 * e.g. total 6 and 2 matching SMS activities → badge 4.
 */
export function patchAppointmentEngageCountsOnMarkActive(
  appt: Appointment,
  subtractCount: number
): boolean {
  const sub = Math.max(0, Math.floor(Number(subtractCount) || 0));
  if (sub <= 0) {
    return false;
  }
  const before = Math.max(0, Math.floor(Number(appt.worksheetEngageIconCount ?? 0)));
  appt.worksheetEngageIconCount = Math.max(0, before - sub);
  appt.worksheetUnreadMessageCount = 0;
  const after = Math.max(0, Math.floor(Number(appt.worksheetEngageIconCount ?? 0)));
  return after !== before;
}

/**
 * After timeline “Dismiss” clears `notification`, decrement worklist row badge fields in place.
 * Returns true when {@link Appointment.worksheetEngageIconCount} changed.
 */
export function patchAppointmentEngageCountsOnActivityDismiss(
  appt: Appointment,
  activityType?: string | null
): boolean {
  const type = String(activityType ?? "").trim();
  if (type && !isEngageDismissBadgeActivityType(type)) {
    return false;
  }
  const before = Math.max(0, Math.floor(Number(appt.worksheetEngageIconCount ?? 0)));
  appt.worksheetEngageIconCount = decCount(appt.worksheetEngageIconCount);
  if (!type || isEngageNotificationsChannelDismissType(type)) {
    appt.worksheetNotificationsChannelCount = decCount(
      appt.worksheetNotificationsChannelCount
    );
  }
  const after = Math.max(0, Math.floor(Number(appt.worksheetEngageIconCount ?? 0)));
  return after !== before;
}

export type WorksheetEngageBadgeDismissChannel = "messages" | "notifications";

/** Recompute Engage icon badge from per-channel counts after a local dismiss. */
export function recomputeWorksheetEngageIconCountFromChannels(
  appt: Appointment
): number {
  const unread = Math.max(0, Number(appt.worksheetUnreadMessageCount ?? 0));
  const notifications = Math.max(
    0,
    Number(appt.worksheetNotificationsChannelCount ?? 0)
  );
  const sum = unread + notifications;
  if (sum > 0) {
    return sum;
  }
  return Math.max(0, Number(appt.worksheetEngageIconCount ?? 0));
}

/** Apply a dismiss/clear to worklist row badge fields (mutates counts on a shallow copy). */
export function patchWorksheetEngageBadgeDismiss(
  appt: Appointment,
  channel: WorksheetEngageBadgeDismissChannel,
  decrement = 1
): Appointment {
  const next = { ...appt };
  if (channel === "messages") {
    next.worksheetUnreadMessageCount = 0;
  } else {
    const cur = Math.max(0, Number(next.worksheetNotificationsChannelCount ?? 0));
    const dec = Math.max(1, Math.floor(Number(decrement) || 1));
    next.worksheetNotificationsChannelCount = Math.max(0, cur - dec);
  }
  const icon = recomputeWorksheetEngageIconCountFromChannels(next);
  if (channel === "notifications" && icon === 0) {
    const prevIcon = Math.max(0, Number(appt.worksheetEngageIconCount ?? 0));
    const dec = Math.max(1, Math.floor(Number(decrement) || 1));
    next.worksheetEngageIconCount = Math.max(0, prevIcon - dec);
  } else {
    next.worksheetEngageIconCount = icon;
  }
  return next;
}
